import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type PlayerSortKey = 'name' | 'level' | 'age';
export type PlayerSortOrder = 'asc' | 'desc';

export type PlayerSortState = {
  key: PlayerSortKey;
  order: PlayerSortOrder;
};

type PlayerSortActions = {
  setSort: (next: Partial<PlayerSortState>) => void;
  reset: () => void;
};

type PlayerSortContextValue = PlayerSortState & PlayerSortActions;

const DEFAULT_SORT: PlayerSortState = {
  key: 'name',
  order: 'asc',
};

const PlayerSortContext = createContext<PlayerSortContextValue | null>(null);

export function PlayerSortProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PlayerSortState>(DEFAULT_SORT);

  const setSort = useCallback((next: Partial<PlayerSortState>) => {
    setState(prev => ({ ...prev, ...next }));
  }, []);

  const reset = useCallback(() => {
    setState(DEFAULT_SORT);
  }, []);

  const value = useMemo<PlayerSortContextValue>(
    () => ({
      ...state,
      setSort,
      reset,
    }),
    [reset, setSort, state],
  );

  return <PlayerSortContext.Provider value={value}>{children}</PlayerSortContext.Provider>;
}

export function usePlayerSort() {
  const ctx = useContext(PlayerSortContext);
  if (!ctx) {
    throw new Error('usePlayerSort must be used within PlayerSortProvider');
  }
  return ctx;
}

export const PLAYER_SORT_DEFAULTS = DEFAULT_SORT;
